import { CHANGE_CRITICAL_RESULT_STATUS } from 'modules/Apollo/queries';
import { useCallback, useMemo } from 'react';
import {
  ChangeCriticalResultStatusMutation,
  ChangeCriticalResultStatusMutationVariables,
  CriticalResultStatus,
} from 'generated/graphql';
import { useMutation } from '@apollo/client';
import { logger } from 'modules/logger';
import { useToasterDispatch } from 'common/ui/Toaster';
import { PermissionName } from '@sironamedical/common/constants/permissionName';
import { useValidateUserHasRbacPermissions } from 'hooks/useValidateUserHasRbacPermissions';
import { NameSpaces, sendCrossTabEvent } from 'modules/EventsManager';
import { getPageType, PageTypes } from 'utils/pageTypes';
import { WithApolloContext } from 'modules/Apollo/types';

type UseCriticalResults = {
  changeCriticalResultStatus: (
    worklistItemSmid: string,
    status: CriticalResultStatus
  ) => Promise<void>;
  isChangeCriticalResultStatusLoading?: boolean;
  hasAccessToCriticalWorkflow: boolean;
};

export const useCriticalResults = ({ context }: WithApolloContext): UseCriticalResults => {
  const { enqueueToast } = useToasterDispatch();
  const hasAccessToCriticalWorkflow = useValidateUserHasRbacPermissions({
    permissionNames: [PermissionName.WorklistCriticalWorkflow],
  });
  const [changeCriticalResultStatusMutation, { loading: isChangeCriticalResultStatusLoading }] =
    useMutation<ChangeCriticalResultStatusMutation, ChangeCriticalResultStatusMutationVariables>(
      CHANGE_CRITICAL_RESULT_STATUS,
      {
        context,
      }
    );

  const formatCriticalResultStatus = useCallback((status: CriticalResultStatus) => {
    switch (status) {
      case CriticalResultStatus.NotCritical:
        return 'Not Critical';
      case CriticalResultStatus.CriticalAcknowledged:
        return 'Critical Acknowledged';
      case CriticalResultStatus.Critical:
        return 'Critical';
      default:
        return 'Not Critical';
    }
  }, []);

  const changeCriticalResultStatus = useCallback(
    async (worklistItemSmid: string, status: CriticalResultStatus) => {
      if (!hasAccessToCriticalWorkflow) {
        logger.warn(
          `[useCriticalResults] Attempted to change critical results status for worklist item ${worklistItemSmid} to ${status}, but user does not have access to workflow`,
          {
            hasAccessToCriticalWorkflow,
          }
        );
        return;
      }

      try {
        await changeCriticalResultStatusMutation({
          variables: { worklistItemSmid, status },
          optimisticResponse: {
            __typename: 'Mutation',
            changeCriticalResultStatus: {
              __typename: 'ChangeCriticalResultStatusResponse',
              smid: worklistItemSmid,
              criticalResultStatus: status,
            },
          },
          update(cache, { data }) {
            const statusData = data?.changeCriticalResultStatus;
            if (!statusData) return;

            sendCrossTabEvent(NameSpaces.WORKLIST_ITEM_PATCH, {
              type: 'worklistItemPatch',
              targetPage:
                getPageType(window.location.href) === PageTypes.REPORTER
                  ? PageTypes.WORKLIST
                  : PageTypes.REPORTER,
              data: {
                id: worklistItemSmid,
                patch: {
                  criticalResultStatus: status,
                },
              },
            });

            cache.modify({
              id: cache.identify({ __typename: 'WorklistItem', smid: statusData.smid }),
              fields: {
                criticalResultStatus: () => statusData.criticalResultStatus,
              },
            });
          },
          onCompleted: (data) => {
            enqueueToast(
              `Successfully changed critical results status for exam ${worklistItemSmid} to ${formatCriticalResultStatus(data?.changeCriticalResultStatus?.criticalResultStatus ?? status)}`,
              {
                severity: 'default',
                position: 'top-right',
                icon: 'greenCheck',
              }
            );

            logger.info(
              `[useCriticalResult] Critical result status of worklist item ${worklistItemSmid} changed to ${status}`
            );
          },
          onError: (error) => {
            enqueueToast(
              `Failed to change critical results status for exam ${worklistItemSmid} to ${formatCriticalResultStatus(status)}`,
              { severity: 'error' }
            );
            logger.error(
              `[useCriticalResult] Error changing critical status of worklist item ${worklistItemSmid} to ${status}`,
              error
            );
          },
        });
      } catch (error) {
        logger.error(
          `[useCriticalResult] Error changing critical status of worklist item ${worklistItemSmid} to ${status}`,
          error
        );

        return;
      }
    },
    [
      changeCriticalResultStatusMutation,
      enqueueToast,
      formatCriticalResultStatus,
      hasAccessToCriticalWorkflow,
    ]
  );

  return useMemo(
    () => ({
      changeCriticalResultStatus,
      isChangeCriticalResultStatusLoading,
      hasAccessToCriticalWorkflow,
    }),
    [changeCriticalResultStatus, isChangeCriticalResultStatusLoading, hasAccessToCriticalWorkflow]
  );
};
